import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { contentAPI, adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, EmptyState } from '../components/ui';
import './ModuleAdmin.css';

const HighlightedEvents = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [form, setForm] = useState({ imageUrl: '', targetUrl: '' });
  const [uploading, setUploading] = useState(false);

  const listQuery = useQuery(['highlighted-events'], async () => {
    const res = await contentAPI.getHighlightedEvents();
    const rows = res.data?.data || [];
    return rows.sort((a, b) => {
      const ao = a.order ?? 0;
      const bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    });
  });

  useEffect(() => {
    setItems(listQuery.data || []);
  }, [listQuery.data]);

  const createMutation = useMutation((payload) => contentAPI.createHighlightedEvent(payload), {
    onSuccess: () => {
      setForm({ imageUrl: '', targetUrl: '' });
      queryClient.invalidateQueries(['highlighted-events']);
    }
  });

  const deleteMutation = useMutation((id) => contentAPI.deleteHighlightedEvent(id), {
    onSuccess: () => queryClient.invalidateQueries(['highlighted-events'])
  });

  const reorderMutation = useMutation((ids) => contentAPI.reorderHighlightedEvents(ids), {
    onSuccess: () => {
      queryClient.invalidateQueries(['highlighted-events']);
      showToast({ message: 'Highlight order saved.', severity: 'success' });
    },
    onError: (err) =>
      showToast({
        message: err.response?.data?.message || 'Failed to save highlight order.',
        severity: 'error'
      })
  });

  const onDragStart = (id) => setDraggingId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => {
    if (!draggingId || draggingId === targetId) return;
    const current = [...items];
    const fromIndex = current.findIndex((row) => row.id === draggingId);
    const toIndex = current.findIndex((row) => row.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);
    setItems(current);
    setDraggingId(null);
  };

  const handleSaveOrder = () => {
    if (!items.length) return;
    reorderMutation.mutate(items.map((row) => row.id));
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url || res.data?.url;
      if (url) {
        setForm((prev) => ({ ...prev, imageUrl: url }));
      } else {
        window.alert('Upload succeeded but no URL returned.');
      }
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!form.imageUrl || !form.targetUrl) return;
    createMutation.mutate({
      imageUrl: form.imageUrl.trim(),
      targetUrl: form.targetUrl.trim(),
      order: items.length
    });
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Highlighted Events"
        subtitle="Manage the image tiles shown in the Highlighted Events strip on the homepage. These are independent of event batches."
      />

      <Card component="form" onSubmit={handleCreate} sx={{ mb: 2 }}>
        <CardContent className="module-form-grid">
          <div>
            <label>Upload Image</label>
            <input type="file" accept="image/*" onChange={handleUploadImage} disabled={uploading} />
            {form.imageUrl && (
              <Box sx={{ mt: 1 }}>
                <img src={form.imageUrl} alt="Preview" style={{ maxWidth: 240, borderRadius: 8 }} />
              </Box>
            )}
          </div>
          <div>
            <label>Target URL</label>
            <input
              type="url"
              placeholder="https://invinciblebeng.o/..."
              value={form.targetUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, targetUrl: e.target.value }))}
              required
            />
          </div>
          <div className="homecat-form-actions">
            <Button
              variant="contained"
              type="submit"
              startIcon={<AddRoundedIcon />}
              disabled={createMutation.isLoading || uploading || !form.imageUrl || !form.targetUrl}
            >
              Add Highlight
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ m: 0 }}>
              Current Highlights
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={!items.length || reorderMutation.isLoading}
              onClick={handleSaveOrder}
            >
              Save Order
            </Button>
          </Box>
          {listQuery.isLoading ? (
            <div className="ops-loading-box">
              <CircularProgress size={28} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading highlighted events…</Typography>
            </div>
          ) : !items.length ? (
            <EmptyState
              title="No highlights configured yet"
              description="Add image tiles using the form above."
            />
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {items.map((row, index) => (
                <Box
                  component="li"
                  key={row.id}
                  draggable
                  onDragStart={() => onDragStart(row.id)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(row.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    p: '0.75rem 1rem',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: String(draggingId) === String(row.id) ? '#f1f5f9' : '#ffffff',
                    cursor: 'grab'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 22, textAlign: 'right', flexShrink: 0 }}>
                      {index + 1}.
                    </Typography>
                    <Box
                      sx={{
                        width: 72,
                        height: 48,
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: '#e2e8f0',
                        flexShrink: 0
                      }}
                    >
                      {row.imageUrl && (
                        <img
                          src={row.imageUrl}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {row.targetUrl}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {row.id}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={() => {
                      if (window.confirm('Remove this highlight?')) {
                        deleteMutation.mutate(row.id);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HighlightedEvents;
